import { ColumnProps, IventisServerSideDataSource } from "@iventis/data-table";
import { v4 as uuid } from "uuid";
import { assign, createMachine } from "xstate";
import { SpatialDataTableContext } from "./sidebar-data-table.types";

type SidebarDataTableState = {
    dataTableKey: string;
    grouping: boolean;
    dataSource: IventisServerSideDataSource;
    isTableReady: boolean;
    gridContext?: SpatialDataTableContext;
    columns: ColumnProps[];
};

type SidebarDataTableActions =
    | { type: "SET_GROUPING"; payload: boolean }
    | { type: "SET_CONTEXT"; payload: SpatialDataTableContext }
    | { type: "SET_COLUMNS"; payload: Partial<Pick<SidebarDataTableState, "columns">> }
    | {
          type: "FILTER_COLUMNS";
          payload: {
              filter: {
                  /** layer id */
                  id: string;
              }[];
          };
      };

/** Hanldes the state in the sidebar data table */
const sidebarDataTableReducer = (state: SidebarDataTableState, action: SidebarDataTableActions): SidebarDataTableState => {
    switch (action.type) {
        case "SET_GROUPING": {
            return {
                ...state,
                dataTableKey: uuid(),
                grouping: action.payload,
            };
        }
        default: {
            return state;
        }
    }
};

export const sidebarDataTableMachine = createMachine(
    {
        id: "sidebar-data-table-machine",
        initial: "getInitialColumns",
        schema: {
            context: {} as SidebarDataTableState,
            events: {} as SidebarDataTableActions,
            services: {} as {
                getInitialColumns: { data: SidebarDataTableState["columns"] };
                filterColumns: { data: SidebarDataTableState["columns"] };
            },
        },
        // eslint-disable-next-line no-undef
        tsTypes: {} as import("./sidebar-data-table-reducer.typegen").Typegen0,
        on: {
            SET_GROUPING: {
                actions: ["setGrouping"],
            },
        },
        states: {
            getInitialColumns: {
                invoke: {
                    src: "getInitialColumns",
                    onDone: {
                        target: "idle",
                        actions: ["setColumns", "setContext"],
                    },
                },
            },
            idle: {
                on: {
                    FILTER_COLUMNS: {
                        target: "filteringColumns",
                    },
                    SET_COLUMNS: {
                        actions: ["setColumns"],
                    },
                },
            },
            filteringColumns: {
                invoke: {
                    src: "filterColumns",
                    onDone: {
                        target: "idle",
                        actions: ["updateColumns"],
                    },
                },
                on: {
                    // If a new request comes in before "updateColumns" is finished it will abort the existing request, restarting the process with the latest request
                    FILTER_COLUMNS: {
                        target: "filteringColumns",
                    },
                },
            },
        },
    },
    {
        actions: {
            setGrouping: assign((context, event) => sidebarDataTableReducer(context, event)),
            setColumns: assign({ columns: (_, event) => (event.type === "SET_COLUMNS" ? event.payload.columns : event.data) }),
            updateColumns: assign({
                columns: (context, event) => {
                    // If the column is not found, then use the new column
                    const newColumns = event.data.map((c) => context.columns.find((col) => c.agGridColumnProps.field === col.agGridColumnProps.field) ?? c);
                    return newColumns;
                },
            }),
        },
    }
);

export const getSidebarDataTableInitialState = (state: Partial<SidebarDataTableState>): SidebarDataTableState => ({
    dataTableKey: uuid(),
    grouping: false,
    isTableReady: false,
    columns: [],
    dataSource: undefined,
    gridContext: undefined,
    ...state,
});
